import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
/* oxlint-disable unicorn/consistent-function-scoping -- large admin page; helpers are colocated for readability */
import { createFileRoute, Link } from "@tanstack/react-router";
import {
    AlertCircle,
    Bell,
    CheckCircle,
    FileText,
    LayoutList,
    Calendar,
    Play,
    RefreshCw,
    Settings,
    Users,
    Wrench,
    UserPlus,
    Link2,
    User,
    CalendarDays,
} from "lucide-react";
import { Suspense, useState } from "react";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import OrganisationsComboBox from "@/features/organisations/components/OrganisationsComboBox";
import Title from "@/components/common/Title";
import { ui } from "@patient-watch/ui";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@patient-watch/ui/accordion";
import { supabaseClient } from "@/lib/supabase";
import { useNav } from "@/utils/nav";
import { cn } from "@/lib/utils";

interface HealthCheckConfig {
    check_type: string;
    error_count: number;
    expected_count: number;
    entity_details: Array<{
        user_id?: string;
        email?: string;
        first_name?: string;
        last_name?: string;
        roles?: string[];
        patient_id?: string;
        diary_id?: string;
        created_at?: string;
        id?: string;
        type?: string;
        diary_name?: string | null;
        event_name?: string | null;
        tracker_name?: string | null;
        form_name?: string | null;
        patient_name?: string | null;
        patient_email?: string | null;
    }> | null;
    first_seen_at: string;
    last_seen_at: string;
    is_resolved: boolean;
    resolved_at: string | null;
    resolved_by: string | null;
}

type EntityDetail = NonNullable<HealthCheckConfig["entity_details"]>[0];

const assignRoleSchema = z.object({
    organisation_id: z.string().min(1, "Please select an organisation").uuid("Invalid organisation"),
    role: z.enum(["PATIENT", "CLINICIAN", "SECRETARY", "ADMIN", "MANAGER"]),
});

type AssignRoleForm = z.infer<typeof assignRoleSchema>;

const ROLE_OPTIONS = [
    { value: "PATIENT", label: "Patient" },
    { value: "CLINICIAN", label: "Clinician" },
    { value: "SECRETARY", label: "Secretary" },
    { value: "ADMIN", label: "Admin" },
    { value: "MANAGER", label: "Manager" },
] as const;

function MissingSharedAttributeCard({
    entity,
    getDiaryIdForEntity,
    getEntityDisplayName,
    onFix,
    isFixing,
}: {
    entity: EntityDetail;
    getDiaryIdForEntity: (e: EntityDetail) => string | null;
    getEntityDisplayName: (e: EntityDetail) => string;
    onFix: () => void;
    isFixing: boolean;
}) {
    const diaryId = getDiaryIdForEntity(entity);
    const entityLabel =
        entity.type === "diary"
            ? "Diary"
            : entity.type === "event"
              ? "Event"
              : entity.type === "tracker"
                ? "Tracker"
                : (entity.type ?? "Entity");
    const entityName = getEntityDisplayName(entity);
    const createdDate = entity.created_at
        ? new Date(entity.created_at).toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
              year: "numeric",
          })
        : null;
    const profileName = entity.patient_name || entity.patient_email || null;

    const isDiary = entity.type === "diary";
    const isTracker = entity.type === "tracker";
    const isEvent = entity.type === "event";
    const today = new Date().toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });

    const TypeIcon = isDiary ? FileText : isTracker ? LayoutList : isEvent ? Calendar : FileText;

    return (
        <div className="bg-card shadow-sm border rounded-xl overflow-hidden">
            {/* Header: type badge + title + fix button */}
            <div className="flex justify-between items-start gap-3 bg-muted/30 p-4 border-b">
                <div className="flex flex-1 items-start gap-3 min-w-0">
                    <div
                        className={cn(
                            "flex justify-center items-center rounded-lg w-10 h-10 shrink-0",
                            isDiary && "bg-blue-100 text-blue-700",
                            isTracker && "bg-amber-100 text-amber-700",
                            isEvent && "bg-emerald-100 text-emerald-700"
                        )}
                    >
                        <TypeIcon className="w-5 h-5" />
                    </div>
                    <div className="space-y-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <span
                                className={cn(
                                    "px-2 py-0.5 rounded-md font-medium text-xs",
                                    isDiary && "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200",
                                    isTracker && "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200",
                                    isEvent &&
                                        "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
                                )}
                            >
                                {entityLabel}
                            </span>
                            {createdDate && (
                                <span className="flex items-center gap-1 text-muted-foreground text-xs">
                                    <CalendarDays className="w-3 h-3" />
                                    Created {createdDate}
                                </span>
                            )}
                        </div>
                        <div className="font-semibold text-base">
                            {diaryId ? (
                                <Link
                                    to="/patients/$diaryId"
                                    params={{ diaryId }}
                                    className="text-primary hover:underline underline-offset-2"
                                >
                                    {entityName || "Unnamed"}
                                </Link>
                            ) : (
                                entityName || "Unnamed"
                            )}
                        </div>
                    </div>
                </div>
                <ui.Button size="sm" variant="secondary" className="shrink-0" onClick={onFix} disabled={isFixing}>
                    {isFixing ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Fix"}
                </ui.Button>
            </div>

            {/* Context: form, patient, linked diary */}
            <div className="space-y-3 p-4">
                <div className="gap-2 grid sm:grid-cols-2">
                    {profileName && (
                        <div className="flex items-center gap-2 bg-muted/50 p-2.5 rounded-lg">
                            <User className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                    Patient (profile)
                                </p>
                                <p className="font-medium text-sm truncate" title={profileName}>
                                    {profileName}
                                </p>
                            </div>
                        </div>
                    )}
                    {isTracker && entity.form_name && (
                        <div className="flex items-center gap-2 bg-muted/50 p-2.5 rounded-lg">
                            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                    Form
                                </p>
                                <p className="font-medium text-sm truncate" title={entity.form_name}>
                                    {entity.form_name}
                                </p>
                            </div>
                        </div>
                    )}
                    {(isTracker || isEvent) && entity.diary_name && (
                        <div
                            className={cn(
                                "flex items-center gap-2 p-2.5 rounded-lg",
                                isTracker
                                    ? "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
                                    : "bg-muted/50"
                            )}
                        >
                            <Link2 className="w-4 h-4 text-amber-600 dark:text-amber-500 shrink-0" />
                            <div className="min-w-0">
                                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                    {isTracker ? "Linked to diary" : "Diary"}
                                </p>
                                <p className="font-medium text-sm truncate" title={entity.diary_name}>
                                    {entity.diary_name}
                                </p>
                            </div>
                        </div>
                    )}
                    {isDiary && entity.diary_name && (
                        <div className="flex items-center gap-2 bg-muted/50 p-2.5 rounded-lg">
                            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                                <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                                    Diary name
                                </p>
                                <p className="font-medium text-sm truncate" title={entity.diary_name}>
                                    {entity.diary_name}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* What Fix will do */}
                <div className="bg-muted/30 p-3 border border-dashed rounded-lg">
                    <p className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wide">
                        What Fix will do
                    </p>
                    {isDiary ? (
                        <ul className="space-y-1.5 text-muted-foreground text-xs">
                            <li className="flex gap-2">
                                <span className="font-medium text-foreground">1.</span>
                                Create a new shared_attribute
                            </li>
                            <li className="flex gap-2">
                                <span className="font-medium text-foreground">2.</span>
                                Set contact_id → {profileName || "patient"}
                            </li>
                            <li className="flex gap-2">
                                <span className="font-medium text-foreground">3.</span>
                                Set start_date & reference_date → {today}
                            </li>
                            <li className="flex gap-2">
                                <span className="font-medium text-foreground">4.</span>
                                Link this diary to the new shared_attribute
                            </li>
                        </ul>
                    ) : (
                        <p className="text-muted-foreground text-xs">
                            Copy shared_attribute_id from parent diary{" "}
                            {entity.diary_name ? (
                                <span className="font-medium text-foreground">&quot;{entity.diary_name}&quot;</span>
                            ) : (
                                "(this diary)"
                            )}{" "}
                            and link it to this {entityLabel.toLowerCase()}.
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}

function AssignRoleFormContent({
    targetUserDisplayName,
    rolelessCount,
    assignSingle,
    onCancel,
    onSubmit,
    isPending,
}: {
    targetUserDisplayName?: string | null;
    rolelessCount?: number;
    assignSingle?: boolean;
    onCancel: () => void;
    onSubmit: (data: AssignRoleForm) => void;
    isPending: boolean;
}) {
    const form = useForm<AssignRoleForm>({
        resolver: zodResolver(assignRoleSchema),
        defaultValues: {
            organisation_id: "",
            role: "PATIENT",
        },
    });

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {targetUserDisplayName && (
                    <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="font-medium text-muted-foreground text-sm">User</p>
                        <p className="font-medium">{targetUserDisplayName}</p>
                    </div>
                )}
                {rolelessCount !== undefined && rolelessCount > 0 && !targetUserDisplayName && (
                    <div className="bg-muted/50 p-3 rounded-lg">
                        <p className="font-medium text-sm">
                            {rolelessCount} user{rolelessCount !== 1 ? "s" : ""} to assign
                        </p>
                    </div>
                )}
                <Suspense fallback={<ui.Skeleton className="w-full h-10" />}>
                    <OrganisationsComboBox />
                </Suspense>
                <fc.Select name="role" label="Role" options={ROLE_OPTIONS} />
                <ui.DialogFooter>
                    <fc.CancelButton variant="outline" size="default" onClick={onCancel} className="w-auto" />
                    <fc.SaveButton
                        buttonType="submit"
                        size="default"
                        label={assignSingle ? "Assign" : "Assign to All"}
                        isLoading={isPending}
                        className="w-auto"
                    />
                </ui.DialogFooter>
            </form>
        </FormProvider>
    );
}

const HealthCheckPage = () => {
    const toastCopy = useToastCopy();
    useNav([
        { label: "Developer", route: { to: "/developer" } },
        {
            label: "Health check",
            route: { to: "/developer/health-check" },
        },
    ]);
    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedConfigs, setExpandedConfigs] = useState<Set<string>>(new Set());
    const [expectedCounts, setExpectedCounts] = useState<Record<string, number>>({});
    const [assignRoleDialogOpen, setAssignRoleDialogOpen] = useState(false);
    const [assignRoleTarget, setAssignRoleTarget] = useState<{
        user_id: string;
        displayName: string;
    } | null>(null);

    // Fetch health check configurations (one row per check type)
    const { data: healthCheckConfigs, isLoading: configsLoading } = useQuery({
        queryKey: ["health-check-configs"],
        queryFn: async () => {
            const { data, error } = await supabaseClient.from("health_checks").select("*").order("check_type");
            if (error) throw error;
            return (data || []) as unknown as HealthCheckConfig[];
        },
    });

    // Resolve error mutation
    const resolveErrorMutation = useMutation({
        mutationFn: async ({ check_type }: { check_type: string }) => {
            const { data, error } = await supabaseClient.functions.invoke("admin-resolve-health-check-error", {
                body: { check_type, entity_id: check_type }, // Using check_type as entity_id for old schema
            });
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            toast.success(toastCopy.errorResolvedSuccess);
            queryClient.invalidateQueries({
                queryKey: ["health-check-configs"],
            });
        },
        onError: (error) => {
            toast.error(toastCopy.failedResolveError(error.message));
        },
    });

    // Trigger health check mutation
    const triggerHealthCheckMutation = useMutation({
        mutationFn: async () => {
            const { data, error } = await supabaseClient.functions.invoke("cron-health-check");
            if (error) throw error;
            return data;
        },
        onSuccess: (data) => {
            toast.success(toastCopy.healthCheckCompleted(data.message));
            queryClient.invalidateQueries({
                queryKey: ["health-check-configs"],
            });
        },
        onError: (error) => {
            toast.error(toastCopy.healthCheckFailed(error.message));
        },
    });

    // Update expected count mutation
    const updateExpectedCountMutation = useMutation({
        mutationFn: async ({ check_type, expected_count }: { check_type: string; expected_count: number }) => {
            const { data, error } = await supabaseClient.functions.invoke("admin-update-expected-count", {
                body: { check_type, expected_count },
            });
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            toast.success(toastCopy.expectedCountUpdatedSuccess);
            queryClient.invalidateQueries({
                queryKey: ["health-check-configs"],
            });
        },
        onError: (error) => {
            toast.error(toastCopy.failedUpdateExpectedCount(error.message));
        },
    });

    // Fix missing shared attributes mutation (all or single)
    const fixMissingSharedAttributesMutation = useMutation({
        mutationFn: async (opts?: { entity_id?: string; type?: string }) => {
            const { data, error } = await supabaseClient.functions.invoke("admin-fix-missing-shared-attributes", {
                body: opts ?? {},
            });
            if (error) throw error;
            return data;
        },
        onSuccess: (data: { fixed?: number; failed?: number }) => {
            toast.success(toastCopy.fixedRecords(data?.fixed ?? 0, data?.failed ?? 0));
            queryClient.invalidateQueries({
                queryKey: ["health-check-configs"],
            });
        },
        onError: (error) => {
            toast.error(toastCopy.failedFixSharedAttributes(error.message));
        },
    });

    // Assign role to roleless users mutation (all or single)
    const assignRoleToRolelessUsersMutation = useMutation({
        mutationFn: async ({
            organisation_id,
            role,
            user_id,
        }: {
            organisation_id: string;
            role: string;
            user_id?: string;
        }) => {
            const { data, error } = await supabaseClient.functions.invoke("admin-assign-role-to-roleless-users", {
                body: { organisation_id, role, user_id },
            });
            if (error) throw error;
            return data;
        },
        onSuccess: (data: { assigned?: number; failed?: number }, { role }) => {
            toast.success(toastCopy.rolesAssignedToUsers(role, data?.assigned ?? 0, data?.failed ?? 0));
            queryClient.invalidateQueries({
                queryKey: ["health-check-configs"],
            });
        },
        onError: (error) => {
            toast.error(toastCopy.failedAssignRole(error.message));
        },
    });

    const handleResolveError = (check_type: string) => {
        resolveErrorMutation.mutate({ check_type });
    };

    const handleTriggerHealthCheck = async () => {
        setIsRefreshing(true);
        try {
            await triggerHealthCheckMutation.mutateAsync();
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleUpdateExpectedCount = (check_type: string) => {
        const expected_count = expectedCounts[check_type];
        if (expected_count !== undefined) {
            updateExpectedCountMutation.mutate({ check_type, expected_count });
        }
    };

    const _toggleConfigExpanded = (check_type: string) => {
        const newExpanded = new Set(expandedConfigs);
        if (newExpanded.has(check_type)) {
            newExpanded.delete(check_type);
        } else {
            newExpanded.add(check_type);
        }
        setExpandedConfigs(newExpanded);
    };

    const _formatJson = (data: unknown) => {
        try {
            return JSON.stringify(data, null, 2);
        } catch {
            return String(data);
        }
    };

    const _getStatusIcon = (isResolved: boolean) => {
        if (isResolved) {
            return <CheckCircle className="w-4 h-4 text-green-600" />;
        }
        return <AlertCircle className="w-4 h-4 text-red-600" />;
    };

    const getCheckTypeIcon = (checkType: string) => {
        const icons: Record<string, React.ReactNode> = {
            messageless_users: <Bell size={20} />,
            diaryless_patients: <FileText size={20} />,
            missing_shared_attributes: <FileText size={20} />,
            knock_profile_missing: <Bell size={20} />,
            unsubscribed_users: <Bell size={20} />,
            roleless_users: <Users size={20} />,
        };
        return icons[checkType] || <AlertCircle size={20} />;
    };

    const getCheckTypeLabel = (checkType: string) => {
        const labels: Record<string, string> = {
            messageless_users: "Messageless Users",
            diaryless_patients: "Diaryless Patients",
            missing_shared_attributes: "Missing Shared Attributes",
            knock_profile_missing: "Knock Profile Missing",
            unsubscribed_users: "Unsubscribed Users",
            roleless_users: "Roleless Users",
        };
        return labels[checkType] || checkType;
    };

    const getEntityDisplayName = (entity: EntityDetail, checkType?: string) => {
        if (checkType === "missing_shared_attributes") {
            if (entity.type === "diary" && entity.diary_name) {
                return entity.diary_name;
            }
            if (entity.type === "event" && entity.event_name) {
                return entity.event_name;
            }
            if (entity.type === "tracker" && entity.tracker_name) {
                return entity.tracker_name;
            }
            const patientPart = entity.patient_name || entity.patient_email;
            if (patientPart && entity.type) {
                return `${entity.type.charAt(0).toUpperCase() + entity.type.slice(1)} – ${patientPart}`;
            }
        }
        if (entity.email) return entity.email;
        if (entity.first_name && entity.last_name) {
            return `${entity.first_name} ${entity.last_name}`;
        }
        if (entity.first_name) return entity.first_name;
        if (entity.id) return entity.id;
        return "Unknown";
    };

    const getEntityDetails = (entity: EntityDetail, checkType?: string) => {
        const info: string[] = [];

        if (checkType === "missing_shared_attributes") {
            if (entity.patient_name) info.push(`Patient: ${entity.patient_name}`);
            if (entity.patient_email) info.push(`Email: ${entity.patient_email}`);
            if (entity.diary_name && entity.type !== "diary") {
                info.push(`Diary: ${entity.diary_name}`);
            }
            if (entity.type) info.push(`Type: ${entity.type}`);
            if (entity.created_at) {
                info.push(`Created: ${new Date(entity.created_at).toLocaleDateString()}`);
            }
            return info;
        }

        if (entity.email) info.push(`Email: ${entity.email}`);
        if (entity.roles && entity.roles.length > 0) {
            info.push(`Roles: ${entity.roles.join(", ")}`);
        }
        if (entity.patient_id) info.push(`Patient ID: ${entity.patient_id}`);
        if (entity.diary_id) info.push(`Diary ID: ${entity.diary_id}`);
        if (entity.type) info.push(`Type: ${entity.type}`);
        if (entity.created_at) {
            info.push(`Created: ${new Date(entity.created_at).toLocaleDateString()}`);
        }
        return info;
    };

    const getDiaryIdForEntity = (entity: EntityDetail): string | null => {
        if (entity.type === "diary" && entity.id) return entity.id;
        if (entity.diary_id) return entity.diary_id;
        return null;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const _getTimeSinceFirstSeen = (dateString: string) => {
        const now = new Date();
        const firstSeen = new Date(dateString);
        const diffInHours = Math.floor((now.getTime() - firstSeen.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        }
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
    };

    // Calculate summary stats
    const totalErrors = healthCheckConfigs?.reduce((sum, config) => sum + config.error_count, 0) || 0;
    const expectedErrors = healthCheckConfigs?.reduce((sum, config) => sum + config.expected_count, 0) || 0;
    const unexpectedErrors =
        healthCheckConfigs?.reduce((sum, config) => sum + Math.max(0, config.error_count - config.expected_count), 0) ||
        0;
    const checkTypesWithErrors = healthCheckConfigs?.filter((config) => config.error_count > 0).length || 0;
    const totalCheckTypes = healthCheckConfigs?.length || 0;

    return (
        <section className="flex flex-col gap-6 p-4">
            <Title
                words="Health Check Management"
                description="Monitor and resolve health check errors across the system."
            />

            {/* Summary Cards */}
            <div className="gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
                <ui.Card>
                    <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                        <ui.CardTitle className="font-medium text-sm">Total Errors</ui.CardTitle>
                        <AlertCircle className="w-4 h-4 text-muted-foreground" />
                    </ui.CardHeader>
                    <ui.CardContent>
                        <div className="font-bold text-2xl">{totalErrors}</div>
                        <p className="text-muted-foreground text-xs">All health check errors</p>
                    </ui.CardContent>
                </ui.Card>

                <ui.Card>
                    <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                        <ui.CardTitle className="font-medium text-sm">Unexpected Errors</ui.CardTitle>
                        <AlertCircle className="w-4 h-4 text-red-600" />
                    </ui.CardHeader>
                    <ui.CardContent>
                        <div className="font-bold text-red-600 text-2xl">{unexpectedErrors}</div>
                        <p className="text-muted-foreground text-xs">Requiring attention</p>
                    </ui.CardContent>
                </ui.Card>

                <ui.Card>
                    <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                        <ui.CardTitle className="font-medium text-sm">Expected Errors</ui.CardTitle>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                    </ui.CardHeader>
                    <ui.CardContent>
                        <div className="font-bold text-green-600 text-2xl">{expectedErrors}</div>
                        <p className="text-muted-foreground text-xs">Marked as expected</p>
                    </ui.CardContent>
                </ui.Card>

                <ui.Card>
                    <ui.CardHeader className="flex flex-row justify-between items-center space-y-0 pb-2">
                        <ui.CardTitle className="font-medium text-sm">Checks with Errors</ui.CardTitle>
                        <Settings className="w-4 h-4 text-amber-600" />
                    </ui.CardHeader>
                    <ui.CardContent>
                        <div className="font-bold text-amber-600 text-2xl">
                            {checkTypesWithErrors}/{totalCheckTypes}
                        </div>
                        <p className="text-muted-foreground text-xs">has errors</p>
                    </ui.CardContent>
                </ui.Card>
            </div>

            {/* Health Check Configurations */}
            <ui.Card>
                <ui.CardHeader className="flex md:flex-row flex-col justify-between items-start gap-2">
                    <div className="flex flex-col gap-2">
                        <ui.CardTitle className="flex items-center gap-2">
                            <Settings className="w-5 h-5" />
                            Health Check Configurations
                        </ui.CardTitle>
                        <ui.CardDescription>
                            Manage expected error counts and view detailed error information.
                        </ui.CardDescription>
                    </div>
                    <ui.Button
                        size="sm"
                        onClick={handleTriggerHealthCheck}
                        disabled={isRefreshing}
                        className="flex items-center gap-2 w-full md:w-fit"
                    >
                        <Play className={cn("w-4 h-4", isRefreshing ? "animate-minibounce" : "")} />
                        {isRefreshing ? "Running Checks" : "Trigger Checks"}
                    </ui.Button>
                </ui.CardHeader>
                <ui.CardContent>
                    {configsLoading ? (
                        <div className="flex justify-center items-center p-8">
                            <RefreshCw className="w-6 h-6 animate-spin" />
                        </div>
                    ) : healthCheckConfigs && healthCheckConfigs.length > 0 ? (
                        <Accordion type="multiple" className="w-full">
                            {healthCheckConfigs.map((config) => (
                                <AccordionItem key={config.check_type} value={config.check_type}>
                                    <AccordionTrigger className="hover:no-underline">
                                        <div className="flex justify-between items-start pr-4 w-full">
                                            <div className="flex items-center gap-3">
                                                {getCheckTypeIcon(config.check_type)}
                                                <div>
                                                    <div className="font-medium text-left">
                                                        {getCheckTypeLabel(config.check_type)}
                                                    </div>
                                                    <div className="text-muted-foreground text-sm text-left">
                                                        Current: {config.error_count} | Expected:{" "}
                                                        {config.expected_count || 0}
                                                        {expectedCounts[config.check_type] !== undefined &&
                                                            expectedCounts[config.check_type] !==
                                                                config.expected_count && (
                                                                <span className="ml-1 text-amber-600">(unsaved)</span>
                                                            )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {/* Status Icons */}
                                                {config.is_resolved && (
                                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                                )}
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                        <div className="space-y-4">
                                            {/* Expected Count Input */}
                                            <div className="flex flex-wrap items-center gap-4 bg-muted/50 p-4 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`text-sm font-medium ${
                                                            config.expected_count === null ||
                                                            config.expected_count === undefined
                                                                ? "text-amber-600"
                                                                : "text-muted-foreground"
                                                        }`}
                                                    >
                                                        Expected Count:
                                                    </span>
                                                    <ui.Input
                                                        type="number"
                                                        min="0"
                                                        value={
                                                            expectedCounts[config.check_type] ??
                                                            config.expected_count ??
                                                            0
                                                        }
                                                        onChange={(e) =>
                                                            setExpectedCounts({
                                                                ...expectedCounts,
                                                                [config.check_type]:
                                                                    Number.parseInt(e.target.value) || 0,
                                                            })
                                                        }
                                                        className={`w-20 h-9 text-sm ${
                                                            config.expected_count === null ||
                                                            config.expected_count === undefined
                                                                ? "border-amber-600 focus:border-amber-600"
                                                                : ""
                                                        }`}
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <ui.Button
                                                        size="sm"
                                                        variant="secondary"
                                                        className="h-9 text-xs"
                                                        onClick={() => handleUpdateExpectedCount(config.check_type)}
                                                        disabled={updateExpectedCountMutation.isPending}
                                                    >
                                                        {updateExpectedCountMutation.isPending ? (
                                                            <RefreshCw className="w-3 h-3 animate-spin" />
                                                        ) : (
                                                            "Save"
                                                        )}
                                                    </ui.Button>
                                                    <ui.Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-9 text-xs"
                                                        onClick={() =>
                                                            setExpectedCounts({
                                                                ...expectedCounts,
                                                                [config.check_type]: config.error_count,
                                                            })
                                                        }
                                                        title="Set expected to current count"
                                                    >
                                                        Set to Current
                                                    </ui.Button>
                                                </div>
                                            </div>

                                            {/* Entity Details */}
                                            {config.entity_details && config.entity_details.length > 0 && (
                                                <div>
                                                    <h4 className="mb-1 font-medium">
                                                        {config.check_type === "missing_shared_attributes"
                                                            ? "Diaries, trackers and events missing shared attributes"
                                                            : "Affected Entities"}{" "}
                                                        ({config.entity_details.length})
                                                    </h4>
                                                    {config.check_type === "missing_shared_attributes" && (
                                                        <p className="mb-3 text-muted-foreground text-sm">
                                                            Each row shows the entity, patient (profile) name, and for
                                                            trackers the form name and linked diary.
                                                        </p>
                                                    )}
                                                    <div className="space-y-3 max-h-96 overflow-auto">
                                                        {config.entity_details.map((entity, index) =>
                                                            config.check_type === "missing_shared_attributes" ? (
                                                                <MissingSharedAttributeCard
                                                                    key={`${config.check_type}-entity-${index}`}
                                                                    entity={entity}
                                                                    getDiaryIdForEntity={getDiaryIdForEntity}
                                                                    getEntityDisplayName={(e) =>
                                                                        getEntityDisplayName(e, config.check_type)
                                                                    }
                                                                    onFix={() =>
                                                                        fixMissingSharedAttributesMutation.mutate({
                                                                            entity_id: entity.id!,
                                                                            type: entity.type!,
                                                                        })
                                                                    }
                                                                    isFixing={
                                                                        fixMissingSharedAttributesMutation.isPending
                                                                    }
                                                                />
                                                            ) : (
                                                                <div
                                                                    key={`${config.check_type}-entity-${index}`}
                                                                    className="flex justify-between items-start gap-2 bg-background p-3 border rounded-lg"
                                                                >
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="font-medium text-sm">
                                                                            {getDiaryIdForEntity(entity) ? (
                                                                                <Link
                                                                                    to="/patients/$diaryId"
                                                                                    params={{
                                                                                        diaryId:
                                                                                            getDiaryIdForEntity(
                                                                                                entity
                                                                                            )!,
                                                                                    }}
                                                                                    className="text-blue-600 hover:text-blue-800 underline underline-offset-2"
                                                                                >
                                                                                    {getEntityDisplayName(
                                                                                        entity,
                                                                                        config.check_type
                                                                                    )}
                                                                                </Link>
                                                                            ) : (
                                                                                getEntityDisplayName(
                                                                                    entity,
                                                                                    config.check_type
                                                                                )
                                                                            )}
                                                                        </div>
                                                                        {getEntityDetails(entity, config.check_type)
                                                                            .length > 0 && (
                                                                            <div className="mt-1 text-muted-foreground text-xs">
                                                                                {getEntityDetails(
                                                                                    entity,
                                                                                    config.check_type
                                                                                ).map((detail, detailIndex) => (
                                                                                    <div
                                                                                        key={`${config.check_type}-detail-${index}-${detailIndex}`}
                                                                                    >
                                                                                        {detail}
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex gap-1 shrink-0">
                                                                        {config.check_type === "roleless_users" &&
                                                                            entity.user_id && (
                                                                                <ui.Button
                                                                                    size="sm"
                                                                                    variant="ghost"
                                                                                    className="h-8 text-xs"
                                                                                    onClick={() => {
                                                                                        setAssignRoleTarget({
                                                                                            user_id: entity.user_id!,
                                                                                            displayName:
                                                                                                getEntityDisplayName(
                                                                                                    entity,
                                                                                                    config.check_type
                                                                                                ),
                                                                                        });
                                                                                        setAssignRoleDialogOpen(true);
                                                                                    }}
                                                                                >
                                                                                    Assign
                                                                                </ui.Button>
                                                                            )}
                                                                    </div>
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Timeline */}
                                            <div className="gap-4 grid grid-cols-2 text-sm">
                                                <div>
                                                    <span className="text-muted-foreground">First Seen:</span>
                                                    <div>{formatDate(config.first_seen_at)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-muted-foreground">Last Seen:</span>
                                                    <div>{formatDate(config.last_seen_at)}</div>
                                                </div>
                                            </div>

                                            {/* Resolution Status */}
                                            {config.is_resolved && (
                                                <div className="flex items-center gap-2 text-green-600 text-sm">
                                                    <CheckCircle size={20} />
                                                    Resolved on {formatDate(config.resolved_at || "")}
                                                    {config.resolved_by && (
                                                        <span className="text-muted-foreground">
                                                            by {config.resolved_by}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Actions */}
                                            <div className="flex flex-wrap gap-2">
                                                {config.check_type === "missing_shared_attributes" &&
                                                    config.error_count > 0 && (
                                                        <ui.Button
                                                            size="sm"
                                                            variant="secondary"
                                                            onClick={() => fixMissingSharedAttributesMutation.mutate()}
                                                            disabled={fixMissingSharedAttributesMutation.isPending}
                                                            className="flex items-center gap-2"
                                                        >
                                                            {fixMissingSharedAttributesMutation.isPending ? (
                                                                <RefreshCw className="w-3 h-3 animate-spin" />
                                                            ) : (
                                                                <Wrench className="w-3 h-3" />
                                                            )}
                                                            Fix All
                                                        </ui.Button>
                                                    )}
                                                {config.check_type === "roleless_users" && config.error_count > 0 && (
                                                    <ui.Button
                                                        size="sm"
                                                        variant="secondary"
                                                        onClick={() => {
                                                            setAssignRoleTarget(null);
                                                            setAssignRoleDialogOpen(true);
                                                        }}
                                                        disabled={assignRoleToRolelessUsersMutation.isPending}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <UserPlus className="w-3 h-3" />
                                                        Assign Role to All
                                                    </ui.Button>
                                                )}
                                                {!config.is_resolved && (
                                                    <ui.Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => handleResolveError(config.check_type)}
                                                        disabled={resolveErrorMutation.isPending}
                                                    >
                                                        Mark as Expected
                                                    </ui.Button>
                                                )}
                                            </div>
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    ) : (
                        <div className="p-8 text-muted-foreground text-center">
                            <CheckCircle className="mx-auto mb-4 w-12 h-12 text-green-600" />
                            <p>No health check configurations found.</p>
                        </div>
                    )}
                </ui.CardContent>
            </ui.Card>

            {/* Assign Role to Roleless Users Dialog */}
            <ui.Dialog open={assignRoleDialogOpen} onOpenChange={setAssignRoleDialogOpen}>
                <ui.DialogContent>
                    <ui.DialogHeader>
                        <ui.DialogTitle>
                            {assignRoleTarget ? "Assign Role to User" : "Assign Role to Roleless Users"}
                        </ui.DialogTitle>
                        <ui.DialogDescription>
                            {assignRoleTarget
                                ? "Choose an organisation and role to assign to this user."
                                : "Assign an organisation and role to all users currently listed as roleless."}
                        </ui.DialogDescription>
                    </ui.DialogHeader>
                    <AssignRoleFormContent
                        targetUserDisplayName={assignRoleTarget?.displayName ?? null}
                        rolelessCount={
                            assignRoleTarget
                                ? null
                                : (healthCheckConfigs?.find((c) => c.check_type === "roleless_users")?.entity_details
                                      ?.length ?? 0)
                        }
                        onCancel={() => {
                            setAssignRoleDialogOpen(false);
                            setAssignRoleTarget(null);
                        }}
                        assignSingle={!!assignRoleTarget}
                        onSubmit={(data) => {
                            assignRoleToRolelessUsersMutation.mutate(
                                {
                                    organisation_id: data.organisation_id,
                                    role: data.role,
                                    user_id: assignRoleTarget?.user_id,
                                },
                                {
                                    onSuccess: () => {
                                        setAssignRoleDialogOpen(false);
                                        setAssignRoleTarget(null);
                                    },
                                }
                            );
                        }}
                        isPending={assignRoleToRolelessUsersMutation.isPending}
                    />
                </ui.DialogContent>
            </ui.Dialog>
        </section>
    );
};

export const Route = createFileRoute("/_app/developer/health-check")({
    component: HealthCheckPage,
});
